import { type BillingOverview, type BillingState, PRICING } from "@kontaktfix/shared";
import { Badge, Button, Card, CardHeader, Skeleton, Stat } from "@kontaktfix/ui";
import { ArrowSquareOutIcon, CreditCardIcon, EnvelopeSimpleIcon, InfoIcon } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { BillingAmount } from "@zuvo-ch/kit/events";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { api } from "@/lib/api";
import { toastError } from "@/lib/errors";
import { daysUntil, formatDate, formatMoney, formatNumber } from "@/lib/i18n";
import { keys, useBilling, useMe } from "@/lib/queries";

const SALES_EMAIL = "hallo@kontaktfix.ch";

const STATE_TONE: Record<BillingState, "neutral" | "accent" | "success" | "warning" | "danger"> = {
  disabled: "neutral",
  trial_pending: "neutral",
  trial: "accent",
  active: "success",
  past_due: "warning",
  expired: "danger",
  canceled: "danger",
};

function StatusText({ billing }: { billing: BillingOverview }) {
  const { t } = useTranslation();
  const sub = billing.subscription;
  if (billing.partner) {
    const partner = billing.partner.name;
    if (billing.partner.endsAt)
      return t("billing.partnerEnds", { partner, date: formatDate(billing.partner.endsAt) });
    if (sub && sub.status !== "canceled" && sub.cancelAtPeriodEnd)
      return `${t("billing.partnerPays", { partner })} ${t("billing.ownEnding", { date: formatDate(sub.currentPeriodEnd) })}`;
    if (billing.state === "active") return t("billing.partnerPays", { partner });
    if (!billing.canUse) return t("billing.partnerMissing", { partner });
  }
  switch (billing.state) {
    case "trial":
      return t("billing.status.trial", {
        date: formatDate(billing.trialEndsAt),
        count: daysUntil(billing.trialEndsAt),
      });
    case "trial_pending":
      return t("billing.status.trial_pending", { days: billing.trialDays });
    case "active":
      return sub?.cancelAtPeriodEnd
        ? t("billing.status.activeEnding", { date: formatDate(sub.currentPeriodEnd) })
        : t("billing.status.active", { date: formatDate(sub?.currentPeriodEnd) });
    case "expired":
      return t("billing.status.expired", { date: formatDate(billing.trialEndsAt) });
    default:
      return t(`billing.status.${billing.state}`);
  }
}

/** Settings → Subscription: trial, number of users, cost, Stripe Checkout and billing portal. */
export function BillingTab() {
  const { t } = useTranslation();
  const search = useSearch({ from: "/app/settings/{-$tab}" });
  const navigate = useNavigate();
  const client = useQueryClient();
  const { data: me } = useMe();
  const returnedFromCheckout = search.checkout === "success";
  const { data: billing } = useBilling({ poll: returnedFromCheckout });
  const [busy, setBusy] = useState<"checkout" | "portal" | null>(null);
  const subscribed = billing?.state === "active" || billing?.state === "past_due";
  const purchaseTracked = useRef(false);

  // Back from Stripe: the webhook may take a moment, so poll until the subscription shows up.
  useEffect(() => {
    if (search.checkout === "cancel") {
      void navigate({ to: "/settings/{-$tab}", params: { tab: "billing" }, replace: true });
    }
    if (returnedFromCheckout && subscribed && billing && !purchaseTracked.current) {
      purchaseTracked.current = true;
      toast.success(t("billing.subscribed"));
      void track("billing.subscription_started", {
        plan: PRICING.plan,
        interval: PRICING.interval,
        amount: billing.estimatedMonthly,
        currency: billing.currency,
      });
      void client.invalidateQueries({ queryKey: keys.me });
      void navigate({ to: "/settings/{-$tab}", params: { tab: "billing" }, replace: true });
    }
  }, [search.checkout, returnedFromCheckout, subscribed, billing, client, navigate, t]);

  const open = async (kind: "checkout" | "portal") => {
    setBusy(kind);
    try {
      const { url, ...amount } = await api.post<{ url: string } & Partial<BillingAmount>>(
        `/api/billing/${kind}`,
      );
      if (kind === "checkout" && amount.plan)
        await track("billing.checkout_started", amount as BillingAmount);
      window.location.assign(url);
    } catch (err) {
      toastError(err);
      setBusy(null);
    }
  };

  if (!billing) return <Skeleton className="h-64" />;
  const volume = billing.billableUsers >= billing.volumeFromUsers;
  const ownRunning = ["active", "trialing", "past_due"].includes(billing.subscription?.status ?? "");
  const canSubscribe = billing.checkoutAvailable && !ownRunning && !me?.supportMode;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title={t("billing.title")}
          description={<StatusText billing={billing} />}
          actions={<Badge tone={STATE_TONE[billing.state]}>{t(`billing.states.${billing.state}`)}</Badge>}
        />
        <div className="grid gap-3 p-5 sm:grid-cols-3">
          <Stat
            label={t("billing.users")}
            value={formatNumber(billing.billableUsers)}
            hint={t("billing.usersHint")}
          />
          <Stat
            label={t("billing.pricePerUser")}
            value={formatMoney(billing.pricePerUser, billing.currency)}
            hint={t("billing.perUserMonth")}
          />
          <Stat
            label={t("billing.estimate")}
            value={formatMoney(billing.estimatedMonthly, billing.currency)}
            hint={t("billing.perMonth")}
          />
        </div>
        {volume && (
          <div className="mx-5 mb-5 flex items-start gap-2.5 rounded-md bg-accent-soft px-3 py-2.5 text-[13px] text-accent">
            <EnvelopeSimpleIcon className="mt-0.5 size-4 shrink-0" />
            <span>
              {t("billing.volume", { count: billing.volumeFromUsers })}{" "}
              <a className="font-medium underline" href={`mailto:${SALES_EMAIL}`}>
                {SALES_EMAIL}
              </a>
            </span>
          </div>
        )}
        {(canSubscribe || billing.portalAvailable) && (
          <div className="flex flex-wrap items-center gap-2 border-t border-border px-5 py-4">
            {canSubscribe && (
              <Button variant="primary" loading={busy === "checkout"} onClick={() => open("checkout")}>
                <CreditCardIcon />
                {t("billing.subscribe")}
              </Button>
            )}
            {billing.portalAvailable && !me?.supportMode && (
              <Button loading={busy === "portal"} onClick={() => open("portal")}>
                <ArrowSquareOutIcon />
                {t("billing.portal")}
              </Button>
            )}
            {returnedFromCheckout && !subscribed && (
              <span className="text-[12.5px] text-fg-muted">{t("billing.waitingForStripe")}</span>
            )}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <div className="flex gap-3 text-[13px] text-fg-muted">
          <InfoIcon className="mt-0.5 size-4 shrink-0 text-fg-subtle" />
          <div className="flex flex-col gap-2">
            <p className="font-medium text-fg">{t("billing.howTitle")}</p>
            <p>{t("billing.howUsers")}</p>
            <p>{t("billing.howBilling")}</p>
            <p>{t("billing.howTrial", { days: billing.trialDays })}</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
